import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2, ArrowLeft, Trophy, HandCoins, Download, Sparkles,
  Target, Users2, Calendar,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const rupees = (paise) => `₹${((paise || 0) / 100).toLocaleString("en-IN")}`;

/**
 * /sponsors/dashboard — Corporate CSR portal for anyone running Match & Multiply
 * pledges. Shows lifetime pledged/matched, active pledges, monthly impact chart,
 * top causes, and a CSV export for compliance.
 */
export default function SponsorsDashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [downloading, setDl] = useState(false);
  useSeo({ title: "CSR Dashboard · find2near",
           description: "Track your Match & Multiply corporate sponsorship — lifetime matched, monthly impact, top causes, compliance-ready CSV export." });

  useEffect(() => {
    if (!user) return;
    api.get("/charity/sponsors/dashboard").then(r => setD(r.data)).catch(e => {
      toast.error(e?.response?.data?.detail || "Couldn't load dashboard");
      setD({ empty: true });
    });
  }, [user]);

  const downloadCsv = async () => {
    setDl(true);
    try {
      const r = await api.get("/charity/sponsors/dashboard.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `csr-match-events-${user.user_id}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("CSV download failed"); }
    finally { setDl(false); }
  };

  const maxMonthly = useMemo(() => {
    if (!d?.monthly_series?.length) return 1;
    return Math.max(1, ...d.monthly_series.map(m => m.paise));
  }, [d?.monthly_series]);

  if (!user) return <div className="min-h-screen grid place-items-center bg-stone-950 text-white">Sign in first.</div>;
  if (!d)    return <div className="min-h-screen grid place-items-center bg-stone-950 text-white"><Loader2 className="animate-spin"/></div>;

  return (
    <div className="min-h-screen bg-gradient-to-b from-fuchsia-950 via-purple-950 to-stone-950 text-white" data-testid="sponsors-dashboard">
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        <Link to="/charity" className="text-[11px] text-white/70 hover:text-white inline-flex items-center gap-1"><ArrowLeft size={12}/> Back to Charity</Link>
        <div>
          <div className="text-[11px] uppercase tracking-widest font-black text-fuchsia-300 flex items-center gap-1"><Trophy size={12}/> Corporate CSR portal</div>
          <h1 className="heading text-3xl md:text-5xl font-black">Sponsor Dashboard</h1>
          <p className="text-sm text-white/70 mt-1">Hi {d.sponsor_name || user.name}, here's how your Match &amp; Multiply pledges are moving the needle.</p>
        </div>

        {/* Hero stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat icon={Target}    label="Lifetime matched"  value={rupees(d.total_matched_paise)} accent="from-fuchsia-500 to-purple-700" testid="stat-matched"/>
          <Stat icon={HandCoins} label="Lifetime pledged"  value={rupees(d.total_pledged_paise)} accent="from-amber-500 to-orange-700"  testid="stat-pledged"/>
          <Stat icon={Sparkles}  label="Active pledges"    value={d.active_pledges}              accent="from-emerald-500 to-teal-700"  testid="stat-active"/>
          <Stat icon={Users2}    label="Causes supported"  value={d.causes_supported}            accent="from-blue-500 to-indigo-700"   testid="stat-causes"/>
        </div>

        {/* Compliance CTA */}
        <div className="rounded-3xl bg-white/5 border border-white/10 p-4 flex items-center gap-3">
          <Download size={22} className="text-white/70"/>
          <div className="flex-1">
            <div className="font-black">Compliance-grade CSV export</div>
            <div className="text-[11px] text-white/60">Every matched event with donation, cause and INR amounts. Ready for your CSR filing.</div>
          </div>
          <Button onClick={downloadCsv} disabled={downloading || (d.match_events || 0) === 0}
                  className="rounded-full bg-white/10 hover:bg-white/20 text-white font-black h-9 text-[11px]" data-testid="sponsors-csv">
            {downloading ? <Loader2 size={13} className="animate-spin mr-1"/> : <Download size={13} className="mr-1"/>}
            Download CSV
          </Button>
        </div>

        {/* Monthly impact chart */}
        <div className="rounded-3xl bg-white/5 border border-white/10 p-4" data-testid="monthly-chart">
          <div className="flex items-center gap-2 mb-3">
            <Calendar size={16} className="text-fuchsia-300"/>
            <div className="font-black">Monthly matched impact (last 12 months)</div>
          </div>
          {(!d.monthly_series || d.monthly_series.length === 0) ? (
            <div className="text-center py-8 text-sm text-white/50" data-testid="monthly-empty">No matches yet. Pledge a Match &amp; Multiply on any cause to start showing here.</div>
          ) : (
            <div className="flex items-end gap-2 h-40">
              {d.monthly_series.map(m => {
                const h = Math.max(4, Math.round((m.paise / maxMonthly) * 100));
                return (
                  <div key={m.month} className="flex-1 flex flex-col items-center justify-end gap-1" data-testid={`month-${m.month}`}>
                    <div className="text-[9px] font-black text-fuchsia-200">{rupees(m.paise)}</div>
                    <div className="w-full rounded-t-lg bg-gradient-to-t from-fuchsia-600 to-fuchsia-300" style={{ height: `${h}%` }}/>
                    <div className="text-[9px] text-white/50">{m.month?.slice(5)}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Top causes */}
        <div className="rounded-3xl bg-white/5 border border-white/10 p-4" data-testid="top-causes">
          <div className="font-black mb-3 flex items-center gap-2"><Trophy size={16} className="text-amber-300"/> Top causes you've matched</div>
          {(!d.top_causes || d.top_causes.length === 0) ? (
            <div className="text-center py-6 text-sm text-white/50">No matched causes yet.</div>
          ) : (
            <div className="space-y-2">
              {d.top_causes.map((c, i) => (
                <div key={c.cause_id} className="flex items-center gap-3 bg-white/5 rounded-2xl p-3" data-testid={`top-cause-${i}`}>
                  <div className="rounded-full w-8 h-8 grid place-items-center bg-fuchsia-500/20 font-black text-fuchsia-300 text-xs">#{i + 1}</div>
                  <div className="flex-1">
                    <div className="font-black text-sm">{c.name}</div>
                    <div className="text-[10px] text-white/60">{c.category?.replace(/_/g, " ")} · {c.city || "any"}</div>
                  </div>
                  <div className="text-fuchsia-300 font-black">{rupees(c.matched_paise)}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Active pledges */}
        <div className="rounded-3xl bg-white/5 border border-white/10 p-4" data-testid="pledges-list">
          <div className="font-black mb-3 flex items-center gap-2"><Sparkles size={16} className="text-emerald-300"/> Your pledges ({d.total_pledges || 0})</div>
          {(!d.pledges || d.pledges.length === 0) ? (
            <div className="text-center py-6 text-sm text-white/50">No pledges yet. Open any cause on /charity and click "Match &amp; Multiply".</div>
          ) : (
            <div className="space-y-1.5">
              {d.pledges.map(p => (
                <div key={p.match_id} className="flex items-center gap-3 text-sm bg-white/5 rounded-xl px-3 py-2" data-testid={`pledge-${p.match_id}`}>
                  <div className={`w-2 h-2 rounded-full ${p.active ? "bg-emerald-400" : "bg-stone-500"}`}/>
                  <div className="flex-1 truncate">
                    <span className="font-black">{p.cause_name || p.cause_id}</span>
                    <span className="text-white/60 text-[11px] ml-2">{p.match_ratio}× · cap {rupees(p.cap_paise)}</span>
                  </div>
                  <div className="text-fuchsia-300 font-black text-xs">matched {rupees(p.matched_paise || 0)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className={`rounded-3xl bg-gradient-to-br ${accent} p-4`} data-testid={testid}>
      <Icon size={18} className="text-white/80"/>
      <div className="text-[10px] uppercase tracking-widest font-black text-white/80 mt-2">{label}</div>
      <div className="text-2xl font-black leading-none mt-1">{value}</div>
    </div>
  );
}
